import React, { useState, useCallback, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Chip,
  InputAdornment,
  IconButton,
  styled
} from "@mui/material";
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcon from '@mui/icons-material/Add';
import { PageTitle } from "../common/PageTitle";
import ModalPopUp from "../common/TransForm/ModalPopUp";
import { Notification } from "../common/Notification";
import { MultiSelectDropdown } from '../Schedule Reports/MultiSelectDropDown';
import axios from "axios";
import { base_url } from '../backendurl/url';
import { Config } from "../../utils/commonFuctions/Token";
import { RefreshToken } from "../../utils/commonFuctions/RefreshToken";
import { useNavigate, useLocation } from "react-router-dom";

// Styled components
const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  padding: '24px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '12px',
  margin: '0 24px 24px 24px'
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 600,
  color: theme.palette.text.primary,
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiInputLabel-root': {
    fontSize: '14px',
  },
}));

const TimingButtonGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px',
  marginTop: '8px',
}));

const TimingButton = styled(Button)(({ theme, selected }) => ({
  padding: '8px 16px',
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '14px',
  border: `1px solid ${selected ? theme.palette.info.main : theme.palette.grey[300]}`,
  backgroundColor: selected ? theme.palette.info.main : 'transparent',
  color: selected ? theme.palette.primary.contrastText : theme.palette.text.primary,
  '&:hover': {
    backgroundColor: selected ? theme.palette.info.dark : theme.palette.grey[100],
    border: `1px solid ${theme.palette.info.dark}`,
  },
}));

const EmailChipsContainer = styled(Box)(({ theme }) => ({
  minHeight: '80px',
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: '8px',
  padding: '12px',
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px',
  alignItems: 'flex-start',
  backgroundColor: theme.palette.background.paper,
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    borderWidth: '2px',
  },
}));

const EmailInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  minWidth: '200px',
  '& .MuiOutlinedInput-root': {
    border: 'none',
    '& fieldset': {
      border: 'none',
    },
  },
  '& .MuiInputBase-input': {
    padding: '8px 0',
  },
}));

const ErrorText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontSize: '12px',
  marginTop: '4px',
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '12px',
  justifyContent: 'flex-start',
  marginTop: '24px',
}));

const SaveButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  color: theme.palette.primary.contrastText,
  padding: '10px 24px',
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
}));

const CancelButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  padding: '10px 24px',
  borderRadius: '6px',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

export const ScheduleReportForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if we're in edit mode
  const isEditMode = location.pathname.includes('/edit/');
  const reportData = location.state?.reportData;
  const reportId = location.pathname.split('/').pop();

 
    const reportHeadlineOptions = [
    { label: 'Payouts', value: 'payouts' },
    { label: 'Players', value: 'players' },
    { label: 'Users', value: 'users' },
    { label: 'Token Transfers', value: 'token_transfers' },
    { label: 'Purchase', value: 'purchases' },
    // { label: 'Access Locations', value: 'access_locations' },
    { label: 'Token Transfer Export', value: 'token_transfer_export' },
    { label: 'Inactive Users Report', value: 'inactive_users_report' },
    { label: 'Top Players', value: 'top_players' },
    { label: 'Pot Details', value: 'pot_details' },
    { label: 'League Details', value: 'league_details' },
    { label: 'User Engagement', value: 'user_engagement' },
    { label: 'Location Users', value: 'location_users' },
  ];
  
  // Form state
  const [formData, setFormData] = useState({
    report_headline:'',
    report_type: [], // Changed to array for multi-select
    report_time: '',
    iteration_timing: 'Weekly',
    to_email: []
  });
  
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [popup, setPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Timing options
  const timingOptions = ['Weekly', 'Monthly', 'Quarterly', 'Yearly'];

  // Initialize form data for edit mode
  useEffect(() => {
    if (isEditMode && reportData) {
      setFormData({
        report_headline: reportData.report_headline || '',
        report_type: Array.isArray(reportData.report_type) 
          ? reportData.report_type 
          : reportData.report_type ? [reportData.report_type] : [],
        report_time: reportData.report_time || '',
        iteration_timing: reportData.iteration_timing || 'Weekly',
        to_email: reportData.to_email || []
      });

      // Set the date picker value
      if (reportData.report_time) {
        setSelectedDateTime(new Date(reportData.report_time));
      }
    }
  }, [isEditMode, reportData]);

  // Format date to display format with actual selected date's day
  const formatDateTimeDisplay = (date) => {
    if (!date) return '';
    
    const options = { 
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    
    return date.toLocaleString('en-US', options);
  };

  const formatDateForAPI = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const tzOffsetMin = date.getTimezoneOffset();
    const sign = tzOffsetMin > 0 ? "-" : "+";
    const offsetHours = String(Math.floor(Math.abs(tzOffsetMin) / 60)).padStart(2, "0");
    const offsetMinutes = String(Math.abs(tzOffsetMin) % 60).padStart(2, "0");

    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const hh = String(date.getHours()).padStart(2, "0");
    const min = String(date.getMinutes()).padStart(2, "0");
    const ss = String(date.getSeconds()).padStart(2, "0");

    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}${sign}${offsetHours}:${offsetMinutes}`;
  };

  // Handle date time change
  const handleDateTimeChange = (newValue) => {
    setSelectedDateTime(newValue);
    if (newValue) {
      const formattedTime = formatDateForAPI(newValue);
      setFormData(prev => ({
        ...prev,
        report_time: formattedTime
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        report_time: ''
      }));
    }
  };

  // Handle timing selection
  const handleTimingSelect = (timing) => {
    setFormData(prev => ({
      ...prev,
      iteration_timing: timing
    }));
  };

  // Handle dropdown change for report headlines
  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Handle email input
  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
    if (emailError) {
      setEmailError('');
    }
  };

  // Email validation
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Add email to list
  const addEmail = () => {
    const email = emailInput.trim();
    if (!email) return;

    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    if (formData.to_email.includes(email)) {
      setEmailError('This email is already added');
      return;
    }

    setFormData(prev => ({
      ...prev,
      to_email: [...prev.to_email, email]
    }));
    setEmailInput('');
    setEmailError('');
  };

  // Handle email input key press
  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === ',') {
      e.preventDefault();
      addEmail();
    }
  };

  // Remove email from list
  const removeEmail = (emailToRemove) => {
    setFormData(prev => ({
      ...prev,
      to_email: prev.to_email.filter(email => email !== emailToRemove)
    }));
  };

  // Handle form submission
  const handleSubmit = () => {
    // Basic validation
    if (!formData.report_headline || formData.report_headline.length === 0) {
      setErrorMessage('Report headline is required');
      setNotification(true);
      return;
    }

    if (!formData.report_type || formData.report_type.length === 0) {
      setErrorMessage('At least one report type is required');
      setNotification(true);
      return;
    }

    if (!formData.report_time) {
      setErrorMessage('Report time is required');
      setNotification(true);
      return;
    }

    if (formData.to_email.length === 0) {
      setErrorMessage('At least one email is required');
      setNotification(true);
      return;
    }

    setPopup(true);
  };

  console.log(formData, "formData");

  // Submit to API
  const submitScheduleReport = async () => {
    try {
      setLoading(true);
      console.log("Submitting schedule report...");
      
      const endpoint = isEditMode 
        ? `${base_url}/api/accounts/admin/scheduler-reports/${reportId}/`
        : `${base_url}/api/accounts/admin/scheduler-reports/`;
      
      const method = isEditMode ? 'put' : 'post';
      
      const response = await axios[method](endpoint, formData, Config);
      
      console.log("Response:", response);
      
      if (response.data?.success === true || response.status === 200 || response.status === 201) {
        setPopup(false);
        setLoading(false);
        
        // Show success notification
        setErrorMessage(isEditMode ? 'Report updated successfully!' : 'Report scheduled successfully!');
        setNotification(true);
        
        // Navigate after a short delay to show the success message
        setTimeout(() => {
          navigate('/schedulereport');
        }, 1500);
      } else {
        throw new Error('Unexpected response format');
      }
      
    } catch (error) {
      setLoading(false);
      setPopup(false);
      
      console.error("Error submitting report:", error);
      
      if (error?.response?.status === 422 && error?.response?.data) {
        setErrorMessage('Validation error occurred');
        setNotification(true);
      } else if (error?.response?.data?.message) {
        setErrorMessage(error.response.data.message);
        setNotification(true);
      } else {
        RefreshToken(error, setErrorMessage, setNotification);
      }
    }
  };

  // Handle cancel
  const handleCancel = () => {
    navigate(-1);
  };

  return (
    <Box>
      <Box display="flex" sx={{ px: 3, pt: 3 }} alignContent="center">
        <PageTitle pageTitle={isEditMode ? "Edit Report" : "Schedule Report"} backBtn />
      </Box>

      <StyledBox>
       <Box>
          <StyledLabel>Report Headline <span style={{color:"red"}}>*</span></StyledLabel>
          <StyledTextField
            fullWidth
            placeholder="Enter headline"
            value={formData.report_headline}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              report_headline: e.target.value
            }))}
            size="small"
          />
        </Box>
        {/* Report Headlines Multi-Select Dropdown */}
        <Box>
          <MultiSelectDropdown
            label="Report Type"
            placeholder="Select report types"
            value={formData.report_type}
            onChange={handleDropdownChange}
            options={reportHeadlineOptions}
            name="report_type"
            validation={{ required: true }}
            showChips={true}
            maxDisplayItems={2}
          />
        </Box>

        {/* Report Time */}
        <Box>
          <StyledLabel>Report Time <span style={{color:"red"}}>*</span></StyledLabel>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              value={selectedDateTime}
              onChange={handleDateTimeChange}
              inputFormat="dd/MM/yyyy hh:mm a"
              renderInput={(params) => (
                <StyledTextField
                  {...params}
                  fullWidth
                  size="small"
                  placeholder="Mon, 2:00 PM"
                  value={formData.report_time || ''}
                  InputProps={{
                    ...params.InputProps,
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton>
                          <CalendarTodayIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </LocalizationProvider>
        </Box>

        {/* Iteration Timing Buttons */}
        <Box>
          <TimingButtonGroup>
            {timingOptions.map((timing) => (
              <TimingButton
                key={timing}
                selected={formData.iteration_timing === timing}
                onClick={() => handleTimingSelect(timing)}
              >
                {timing}
              </TimingButton>
            ))}
          </TimingButtonGroup>
        </Box>

        {/* Email Input */}
        <Box>
          <StyledLabel>Enter Emails to send report <span style={{color:"red"}}>*</span></StyledLabel>
          <EmailChipsContainer>
            {formData.to_email.map((email, index) => (
              <Chip
                key={index}
                label={email}
                onDelete={() => removeEmail(email)}
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: '#e3f2fd',
                  borderColor: '#2196f3',
                  '& .MuiChip-deleteIcon': {
                    color: '#2196f3',
                  },
                }}
              />
            ))}
            <EmailInput
              placeholder="Enter email address"
              value={emailInput}
              onChange={handleEmailInputChange}
              onKeyPress={handleEmailKeyPress}
              onBlur={addEmail}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: emailInput && (
                  <InputAdornment position="end">
                    <IconButton onClick={addEmail} size="small">
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </EmailChipsContainer>
          
          {emailError && (
            <ErrorText>{emailError}</ErrorText>
          )}
          
          {formData.to_email.length > 0 && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              Sending report to {formData.to_email.length} recipient{formData.to_email.length > 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {/* Action Buttons */}
        <ActionButtonsContainer>
          <SaveButton onClick={handleSubmit} disabled={loading}>
            {isEditMode ? 'Update' : 'Save'}
          </SaveButton>
          <CancelButton onClick={handleCancel}>
            Cancel
          </CancelButton>
        </ActionButtonsContainer>
      </StyledBox>

      {/* Confirmation Modal */}
      <ModalPopUp
        openpopup={popup}
        setPopup={setPopup}
        title={isEditMode ? "Confirm Update Report" : "Confirm Schedule Report"}
        subTitle={isEditMode 
          ? "Are you sure you want to update this report with the given details?" 
          : "Are you sure you want to schedule this report with the given details?"
        }
        onClick={submitScheduleReport}
        save={isEditMode ? "Yes, Update" : "Yes, Schedule"}
        cancel="Cancel"
        loading={loading}
      />

      {/* Error/Success Notification */}
      <Notification 
        message={errorMessage} 
        setopen={setNotification} 
        type={errorMessage.includes('successfully') ? 'success' : 'error'} 
        show={notification} 
      />
    </Box>
  );
};